/**
 * Admin Features Page
 * Feature flags management interface for super_admin only
 * Allows dynamic control of platform features (ads, wishes, products, shopping, navigation)
 *
 * Part of Task 10: Modular Feature Toggle System
 * Reference: project/plans/2025-03-27-modular-feature-toggle-system.md
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureConfig } from '../../../contexts/FeatureContext';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import api from '../../../services/api';
import type { FeatureConfig } from '../../../types';
import styles from './AdminFeaturesPage.module.css';

/**
 * Default feature configuration (all enabled)
 */
const DEFAULT_FEATURES: FeatureConfig = {
  platforms: {
    advertising: true,
    ecommerce: true,
  },
  ads: {
    listing: true,
    detail: true,
    create: true,
  },
  wishes: {
    listing: true,
    detail: true,
    create: true,
  },
  products: {
    listing: true,
    detail: true,
  },
  shopping: {
    cart: true,
    checkout: true,
    orders: true,
  },
  navigation: {
    showAdsLink: true,
    showWishesLink: true,
    showProductsLink: true,
  },
  maintenance: {
    enabled: false,
    message: '網站正在升級中，請稍後再來。',
    allowAdminAccess: true,
  },
};

/**
 * Admin Features Page Component
 *
 * Provides UI for super_admin to:
 * - View current feature flag configuration
 * - Toggle individual features on/off
 * - Reset to default values (all enabled)
 * - Save changes to backend KV storage
 */
const AdminFeaturesPage = () => {
  const { hasRole } = useAuth();
  const { config, loading, error, refetch } = useFeatureConfig();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [localConfig, setLocalConfig] = useState<FeatureConfig | null>(null);

  // Check permissions
  useEffect(() => {
    if (!hasRole(['super_admin'])) {
      setMessage({ type: 'error', text: '您需要超級管理員權限才能訪問此頁面' });
    }
  }, [hasRole]);

  // Initialize local config when loaded
  useEffect(() => {
    if (config) {
      setLocalConfig(config);
    }
  }, [config]);

  /**
   * Update nested value in config object by dot-notation path
   * Example: updateNestedValue(config, 'platforms.advertising', false)
   */
  const updateNestedValue = (obj: FeatureConfig, path: string, value: boolean | string): FeatureConfig => {
    const keys = path.split('.') as Array<string>;
    const result = structuredClone(obj) as FeatureConfig;

    // Traverse to the nested object, creating missing objects
    let current: Record<string, unknown> = result as unknown as Record<string, unknown>;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (current[key] && typeof current[key] === 'object') {
        current = current[key] as Record<string, unknown>;
      } else {
        // Create missing object
        current[key] = {};
        current = current[key] as Record<string, unknown>;
      }
    }
    current[keys[keys.length - 1]] = value;

    return result;
  };

  /**
   * Handle text input change for maintenance message
   */
  const handleTextChange = (path: string, value: string) => {
    if (!localConfig) return;

    const updated = updateNestedValue(localConfig, path, value);
    setLocalConfig(updated);
  };

  /**
   * Handle toggle change - update local state immediately
   */
  const handleToggle = (path: string, value: boolean) => {
    if (!localConfig) return;

    const updated = updateNestedValue(localConfig, path, value);
    setLocalConfig(updated);
  };

  /**
   * Save current configuration to backend
   */
  const saveConfig = async () => {
    if (!localConfig) return;

    setSaving(true);
    setMessage(null);

    try {
      const response = await api.put<FeatureConfig>('/api/features', localConfig);

      if (response.success && response.data) {
        await refetch();
        setMessage({ type: 'success', text: '功能配置已儲存' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: response.error?.message || '儲存失敗' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '網路錯誤，請稍後再試' });
      console.error('Save config error:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset all features to default (enabled) state
   */
  const resetToDefaults = () => {
    if (confirm('確定要重設所有功能為預設值（全部啟用）？')) {
      setLocalConfig(DEFAULT_FEATURES);
      setMessage({ type: 'success', text: '已重設為預設值，請點擊儲存以生效' });
    }
  };

  // Permission denied
  if (!hasRole(['super_admin'])) {
    return (
      <div className={styles.adminFeaturesPage}>
        <DesktopLayout>
          <main className={styles.adminFeaturesPage}>
            <div className={styles.message + ' ' + styles.messageError}>
              權限不足：此頁面僅供超級管理員使用
            </div>
          </main>
        </DesktopLayout>
      </div>
    );
  }

  // Loading state
  if (loading || !localConfig) {
    return (
      <div className={styles.adminFeaturesPage}>
        <DesktopLayout>
          <main className={styles.adminFeaturesPage}>
            <div className={styles.loading}>載入中...</div>
          </main>
        </DesktopLayout>
      </div>
    );
  }

  // Error state
  if (error && !localConfig) {
    return (
      <div className={styles.adminFeaturesPage}>
        <DesktopLayout>
          <main className={styles.adminFeaturesPage}>
            <div className={styles.message + ' ' + styles.messageError}>
              載入失敗：{error}
            </div>
          </main>
        </DesktopLayout>
      </div>
    );
  }

  return (
    <div className={styles.adminFeaturesPage}>
      <DesktopLayout>
        <main>
          <div className={styles.adminFeaturesPage}>
            <header className={styles.header}>
              <h1 className={styles.title}>功能開關管理</h1>
              <p className={styles.subtitle}>控制各項功能的啟用狀態</p>
            </header>

            {message && (
              <div className={`${styles.message} ${styles.messageSuccess}`}>
                {message.text}
              </div>
            )}

            {/* Ads Module */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>📢 廣告模組</h2>
              <ToggleRow
                name="廣告列表"
                desc="顯示廣告列表頁面"
                checked={localConfig.ads.listing}
                onChange={(value) => handleToggle('ads.listing', value)}
              />
              <ToggleRow
                name="廣告詳情"
                desc="顯示廣告詳情頁面"
                checked={localConfig.ads.detail}
                onChange={(value) => handleToggle('ads.detail', value)}
              />
              <ToggleRow
                name="發布廣告"
                desc="允許會員發布新廣告"
                checked={localConfig.ads.create}
                onChange={(value) => handleToggle('ads.create', value)}
              />
              <ToggleRow
                name="廣告連結"
                desc="在導航選單顯示廣告連結"
                checked={localConfig.navigation.showAdsLink}
                onChange={(value) => handleToggle('navigation.showAdsLink', value)}
              />
            </section>

            {/* Wishes Module */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>⭐ 許願池模組</h2>
              <ToggleRow
                name="許願池列表"
                desc="顯示許願池列表頁面"
                checked={localConfig.wishes.listing}
                onChange={(value) => handleToggle('wishes.listing', value)}
              />
              <ToggleRow
                name="許願詳情"
                desc="顯示許願詳情頁面"
                checked={localConfig.wishes.detail}
                onChange={(value) => handleToggle('wishes.detail', value)}
              />
              <ToggleRow
                name="發布許願"
                desc="允許會員發布新許願"
                checked={localConfig.wishes.create}
                onChange={(value) => handleToggle('wishes.create', value)}
              />
              <ToggleRow
                name="許願池連結"
                desc="在導航選單顯示許願池連結"
                checked={localConfig.navigation.showWishesLink}
                onChange={(value) => handleToggle('navigation.showWishesLink', value)}
              />
            </section>

            {/* Products Module */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🛍️ 商品模組</h2>
              <ToggleRow
                name="商品列表"
                desc="顯示商品列表頁面"
                checked={localConfig.products.listing}
                onChange={(value) => handleToggle('products.listing', value)}
              />
              <ToggleRow
                name="商品詳情"
                desc="顯示商品詳情頁面"
                checked={localConfig.products.detail}
                onChange={(value) => handleToggle('products.detail', value)}
              />
              <ToggleRow
                name="商品連結"
                desc="在導航選單顯示商品連結"
                checked={localConfig.navigation.showProductsLink}
                onChange={(value) => handleToggle('navigation.showProductsLink', value)}
              />
            </section>

            {/* Shopping Module */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🛒 購物模組</h2>
              <ToggleRow
                name="購物車"
                desc="啟用購物車功能"
                checked={localConfig.shopping.cart}
                onChange={(value) => handleToggle('shopping.cart', value)}
              />
              <ToggleRow
                name="結帳"
                desc="啟用結帳流程"
                checked={localConfig.shopping.checkout}
                onChange={(value) => handleToggle('shopping.checkout', value)}
              />
              <ToggleRow
                name="訂單管理"
                desc="啟用訂單查詢功能"
                checked={localConfig.shopping.orders}
                onChange={(value) => handleToggle('shopping.orders', value)}
              />
            </section>

            {/* System Settings */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>⚙️ 系統設定</h2>
              <ToggleRow
                name="廣告媒合平台"
                desc="控制廣告和許願池模組"
                checked={localConfig.platforms.advertising}
                onChange={(value) => handleToggle('platforms.advertising', value)}
              />
              <ToggleRow
                name="電商平台"
                desc="控制商品和購物模組"
                checked={localConfig.platforms.ecommerce}
                onChange={(value) => handleToggle('platforms.ecommerce', value)}
              />
            </section>

            {/* Maintenance Mode Settings */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🚧 施工中設定</h2>
              <div className={styles.warningBox}>
                <p>⚠️ 開啟施工中模式後，所有非管理員用戶將看到施工中頁面</p>
              </div>
              <ToggleRow
                name="啟用施工中模式"
                desc="開啟後，所有非管理員用戶將看到施工中頁面"
                checked={localConfig.maintenance?.enabled ?? false}
                onChange={(value) => handleToggle('maintenance.enabled', value)}
              />
              {(localConfig.maintenance?.enabled ?? false) && (
                <>
                  <div className={styles.inputRow}>
                    <label className={styles.inputLabel}>自訂訊息</label>
                    <input
                      type="text"
                      className={styles.inputField}
                      value={localConfig.maintenance?.message || ''}
                      onChange={(e) => handleTextChange('maintenance.message', e.target.value)}
                      placeholder="網站正在升級中，請稍後再來。"
                    />
                  </div>
                  <ToggleRow
                    name="允許管理員訪問"
                    desc="管理員可以正常訪問網站進行測試"
                    checked={localConfig.maintenance?.allowAdminAccess ?? true}
                    onChange={(value) => handleToggle('maintenance.allowAdminAccess', value)}
                  />
                </>
              )}
            </section>

            {/* Action Buttons */}
            <div className={styles.actions}>
              <button
                className={styles.resetButton}
                onClick={resetToDefaults}
                disabled={saving}
              >
                重設為預設值
              </button>
              <button
                className={styles.saveButton}
                onClick={saveConfig}
                disabled={saving}
              >
                {saving ? '儲存中...' : '儲存變更'}
              </button>
            </div>
          </div>
        </main>
      </DesktopLayout>
    </div>
  );
};

/**
 * Toggle Row Component
 *
 * Individual toggle switch with label and description
 */
interface ToggleRowProps {
  name: string;
  desc?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const ToggleRow = ({ name, desc, checked, onChange }: ToggleRowProps) => {
  return (
    <div className={styles.toggleRow}>
      <div className={styles.toggleLabel}>
        <span className={styles.toggleName}>{name}</span>
        {desc && <span className={styles.toggleDesc}>{desc}</span>}
      </div>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className={styles.toggleSlider}></span>
      </label>
    </div>
  );
};

export default AdminFeaturesPage;
